import type { ComponentPropsWithRef } from "react";
import { forwardRef, useMemo } from "react";
import { Hr, Section } from "@react-email/components";
import { useGetMarketingEmailAddressSettings } from "@/react/components/SettingsApp/Emails/hooks/useGetMarketingEmailAddressSettings";
import type { StyleValue } from "@/react/components/shared/BuilderUI/StyleValue.types";
import { usePunditUserContext } from "@/react/contexts/punditUserContext";
import { useParseStyles } from "../../../Design/useParseStyles";
import { defaultStyles } from "./defaultStyles";
import { AddressSection } from "./sections/AddressSection";
import { CommunityUrlSection } from "./sections/CommunityUrlSection";
import { LogoSection } from "./sections/LogoSection";
import { PreferenceSection } from "./sections/PreferenceSection";

interface FooterBlockProps extends ComponentPropsWithRef<"table"> {
  "data-preview-mode": boolean;
  logo: boolean;
  styles?: Record<string, StyleValue>;
}

const footerBlockDefaultStyles = {
  margin: defaultStyles.margin,
  padding: defaultStyles.padding,
  backgroundColor: defaultStyles.backgroundColor,
  fontSize: defaultStyles.content.fontSize,
  fontWeight: defaultStyles.content.fontWeight,
  textAlign: defaultStyles.content.textAlign,
  color: defaultStyles.content.color,
} as const;

export const FooterBlockComponent = forwardRef<
  HTMLTableElement,
  FooterBlockProps
>(
  (
    {
      className,
      styles = {},
      logo: showLogo,
      "data-preview-mode": isPreviewMode,
      ...rest
    },
    ref,
  ) => {
    const { data: footerData, isLoading: isLoadingFooter } =
      useGetMarketingEmailAddressSettings();
    const { currentCommunity, isLoading: isLoadingCommunity } =
      usePunditUserContext();

    const sectionStyles = useParseStyles({
      styles: useMemo(() => {
        const {
          margin: marginStyles,
          padding: paddingStyles,
          backgroundColor: backgroundColorStyles,
          fontSize: fontSizeStyles,
          fontWeight: fontWeightStyles,
          textAlign: textAlignStyles,
          color: colorStyles,
        } = styles;
        return {
          margin: marginStyles,
          padding: paddingStyles,
          backgroundColor: backgroundColorStyles,
          fontSize: fontSizeStyles,
          fontWeight: fontWeightStyles,
          textAlign: textAlignStyles,
          color: colorStyles,
        };
      }, [styles]),
      defaultStyles: footerBlockDefaultStyles,
    });

    if (isLoadingFooter || isLoadingCommunity || !currentCommunity) {
      return null;
    }

    return (
      <Section
        // id is used to identify the footer block in the HTML content.
        // Changing the id will break the links analytics for the email.
        // If you need to change the id, you must also update the footer block in the LinksFromHtmlService.
        id="email-footer-block"
        style={sectionStyles}
        ref={ref}
        className={className}
        data-no-drop-below="true"
        {...rest}
      >
        <Hr style={defaultStyles.content.line} />
        {showLogo && (
          <LogoSection
            logoUrl={currentCommunity?.logo_url}
            iconUrl={currentCommunity?.icon_url}
            communityName={currentCommunity?.name}
          />
        )}
        <CommunityUrlSection
          rootUrl={currentCommunity?.root_url}
          isPreviewMode={isPreviewMode}
          styles={defaultStyles.content.community}
        />
        <AddressSection
          address={footerData?.formatted_address || null}
          styles={defaultStyles.content.address}
        />
        <PreferenceSection
          isPreviewMode={isPreviewMode}
          styles={defaultStyles.content.preferences}
        />
      </Section>
    );
  },
);

FooterBlockComponent.displayName = "FooterBlockComponent";
